import SearchIcon from "@mui/icons-material/Search";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Switch from "@mui/material/Switch";
import { setNestedObjectValues, useFormik } from "formik";
import _ from "lodash";
import { computed } from "mobx";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useChangeLeadStatusStore, useLeadStore, usePipedriveStore, usePopupStore, useSearchStore, useSetReminderStore, useViewSettingsStore } from "../..";
import useClosable from "../../hooks/useClosable";
import useLeadNoteCreate from "../../hooks/useLeadNoteCreate";
import useLeadUpdate from "../../hooks/useLeadUpdate";
import useNavigateToCompany from "../../hooks/useNavigateToCompany";
import useNavigateToLead from "../../hooks/useNavigateToLead";
import useRecentlyViewed from "../../hooks/useRecentlyViewed";
import useValidateLeadUpdate from "../../hooks/useValidateLeadUpdate";
import { ILeadEditState, LeadEditState, LeadStatus } from "../../interfaces/ILead";
import { RecentItemType } from "../../interfaces/IRecentItem";
import SearchType from "../../interfaces/SearchType";
import { ClosableOrder } from "../../store/ClosableStore";
import { leadOwnershipTypeToString } from "../../utils/enumUtils";
import { formatDate, formatDateTime, formatNumber, formatPrice } from "../../utils/utils";
import commonStyles from "../Common.module.css";
import EditControls from "../EditControls";
import { LeadFavoriteCheckbox } from "../favorites/FavoriteCheckbox";
import FieldRow from "../FieldRow";
import DealIcon from "../icons/DealIcon";
import ExpandIcon from "../icons/ExpandIcon";
import LeadIcon from "../icons/LeadIcon";
import PipedriveIcon from "../icons/PipedriveIcon";
import DateInput from "../inputs/DateInput";
import { PhoneNumberInputRow } from "../inputs/PhoneNumberInput";
import PipedriveCompanyInput from "../inputs/PipedriveCompanyInput";
import PipedriveLabelSelect from "../inputs/PipedriveLabelSelect";
import { StringInputRow } from "../inputs/StringInput";
import MapPopupWithFlyTo from "../MapPopupWithFlyTo";
import Note from "../Note";
import popupStyles from "../Popup.module.css";
import OnMapSpinner from "../spinners/OnMapSpinner";
import styles from "./LeadPopup.module.css";
import LeadStatusWithIcon from "./LeadStatusWithIcon";

function LeadPopup({ id }: ILeadPopupProps) {
    const updateLead = useLeadUpdate();
    const createLeadNote = useLeadNoteCreate();
    const validateLead = useValidateLeadUpdate();
    const navigateToLead = useNavigateToLead();
    const navigateToCompany = useNavigateToCompany();
    const leadStore = useLeadStore();
    const popupStore = usePopupStore();
    const searchStore = useSearchStore();
    const pipedriveStore = usePipedriveStore();
    const setReminderStore = useSetReminderStore();
    const changeLeadStatusStore = useChangeLeadStatusStore();
    const viewSettingsStore = useViewSettingsStore();
    const lead = useMemo(() => computed(() => leadStore.leads.find(l => l.id === id)), [id]).get();
    const activity = useMemo(() => computed(() => pipedriveStore.activities.find(a => a.id === lead?.activityId)), [lead]).get();
    const pipedriveOrganization = useMemo(() => computed(() => lead && pipedriveStore.organizations.find(o => o.id === lead.pipedriveCompanyId)), [lead]).get();
    const labelOption = useMemo(() => computed(() => pipedriveStore.labelOptions.find(o => o.id === lead?.labelId)), [lead]).get();
    const latestNote = lead && _.maxBy(lead.notes, n => n.createdAt);
    useRecentlyViewed(Number(id), RecentItemType.Lead);

    const close = () => popupStore.close();
    useClosable(ClosableOrder.Medium, close);

    const formik = useFormik<ILeadEditState>({
        initialValues: LeadEditState.empty(),
        validate: validateLead,
        onSubmit: async (values) => {
            await updateLead(lead!, values);
            close();
        },
    });

    const changeStatus = async (status: LeadStatus, note: string) => {
        const values = { ...formik.values, status };
        const errors = await formik.validateForm(values);
        if (Object.keys(errors).length > 0) {
            formik.setTouched(setNestedObjectValues(errors, true));
            return;
        }

        await updateLead(lead!, values);
        await createLeadNote(lead!.id, note);
        close();
    };

    useEffect(() => {
        if (lead && !leadStore.isLeadsLoading.updating) {
            formik.resetForm({
                values: {
                    status: lead.status,
                    labelId: lead.labelId,
                    address: lead.address,
                    zoning: lead.zoning,
                    landSize: lead.landSize,
                    usableArea: lead.usableArea,
                    buildingSize: lead.buildingSize,
                    lotCoverage: lead.lotCoverage,
                    totalAssessment: lead.totalAssessment,
                    ownerName: lead.ownerName,
                    ownershipType: lead.ownershipType,
                    ownerAddress: lead.ownerAddress,
                    saleDate: lead.saleDate,
                    salePrice: lead.salePrice,
                    pipedriveCompanyName: pipedriveOrganization?.name || null,
                    contactName: lead.contactName,
                    email: lead.email,
                    phoneNumber: lead.phoneNumber,
                    isSetReminder: !!lead.activityId && !!activity,
                    activityId: activity?.id || null,
                    activityDueDate: activity?.dueDate || null,
                    activitySubject: activity?.subject || null,
                },
            });
        }
    }, [lead, activity, pipedriveOrganization]);

    if (leadStore.isLeadsLoading.fetching || pipedriveStore.isActivitiesLoading.fetching)
        return <OnMapSpinner />;

    if (!lead)
        return null;

    const isReadOnly = leadStore.editedLeadId === lead.id;

    return (
        <>
            <MapPopupWithFlyTo
                longitude={lead.centerPoint.longitude}
                latitude={lead.centerPoint.latitude}
                closeButton={true}
                closeOnClick={false}
                onClose={close}
                anchor="left"
            >
                <div className={popupStyles.header}>
                    <LeadIcon color="white" />
                    <div className={popupStyles.titleClickable} onClick={() => navigateToLead(id, true)}>
                        <div>{lead.address}</div>
                        <span className={popupStyles.headerDescription}>{lead.city} {lead.state}</span>
                    </div>
                    <LeadFavoriteCheckbox id={Number(id)} className={[popupStyles.headerLink, popupStyles.favoriteIcon].join(" ")} />
                    <Link to={`/deals/createFromLead/${id}`} title="Add as Deal">
                        <DealIcon color="white" className={popupStyles.headerLink} />
                    </Link>
                    {activity && (
                        <a target="_blank" href={pipedriveStore.getLinkToActivity(activity.id, activity.userId)} title="View Activity in Pipedrive">
                            <PipedriveIcon color="white" className={popupStyles.headerLink} />
                        </a>
                    )}
                </div>
                <div className={styles.selects}>
                    {isReadOnly ? (
                        <>
                            <LeadStatusWithIcon status={lead.status as LeadStatus} />
                            {labelOption && <span>{labelOption.label}</span>}
                        </>
                    ) : (
                        <>
                            <Select value={formik.values.status} onChange={e => changeLeadStatusStore.open(e.target.value as LeadStatus, changeStatus)} className={styles.select}>
                                {Object.values(LeadStatus).map(status => (
                                    <MenuItem key={status} value={status} className={styles.select}>
                                        <LeadStatusWithIcon status={status} />
                                    </MenuItem>
                                ))}
                            </Select>
                            <PipedriveLabelSelect formik={formik} name="labelId" className={styles.select} />
                        </>
                    )}
                </div>
                <div className={commonStyles.separator}></div>
                <div className={[popupStyles.body, !viewSettingsStore.isPopupExpanded && styles.scrollable].join(" ")}>
                    <FieldRow label="OWNER NAME">
                        {lead.ownerName && (
                            <span className={commonStyles.link} onClick={() => lead.ownerName && searchStore.setSearch(lead.ownerName, SearchType.Leads)}>
                                {lead.ownerName}
                            </span>
                        )}
                    </FieldRow>
                    <FieldRow label="OWNERSHIP TYPE">
                        {!!lead.ownershipType && leadOwnershipTypeToString(lead.ownershipType)}
                    </FieldRow>
                    <FieldRow label="ZONING">
                        {lead.zoning}
                    </FieldRow>
                    <FieldRow label="LAND SIZE">
                        {!!lead.landSize && `${formatNumber(lead.landSize)} Acres`}
                    </FieldRow>
                    <FieldRow label="USABLE LAND SIZE">
                        {!!lead.usableArea && `${formatNumber(lead.usableArea)} Acres`}
                    </FieldRow>
                    <FieldRow label="BUILDING SIZE">
                        {!!lead.buildingSize && `${formatNumber(lead.buildingSize)} Sq-Ft`}
                    </FieldRow>
                    <FieldRow label="LOT COVERAGE">
                        {!!lead.lotCoverage && `${lead.lotCoverage}%`}
                    </FieldRow>
                    <FieldRow label="LOCATION GRADE">
                        {lead.locationGrade}
                    </FieldRow>
                    <FieldRow label="PROPERTY GRADE">
                        {lead.propertyGrade}
                    </FieldRow>
                    <FieldRow label="ASSESSMENT">
                        {!!lead.totalAssessment && formatPrice(lead.totalAssessment)}
                    </FieldRow>
                    <FieldRow label="OWNER ADDRESS">
                        {lead.ownerAddress && (
                            <span className={commonStyles.link} onClick={() => lead.ownerAddress && searchStore.setSearch(lead.ownerAddress, SearchType.Leads)}>
                                {lead.ownerAddress}
                            </span>
                        )}
                    </FieldRow>
                    <FieldRow label="LAST SOLD">
                        {(lead.saleDate || lead.salePrice) && [
                            !!lead.saleDate && formatDate(lead.saleDate),
                            !!lead.salePrice && formatPrice(lead.salePrice),
                        ].filter(s => s).join(": ")}
                    </FieldRow>
                    {isReadOnly ? (
                        <>
                            <FieldRow label="COMPANY">
                                {pipedriveOrganization && (
                                    <span className={commonStyles.link} onClick={() => navigateToCompany(pipedriveOrganization.id)}>
                                        {pipedriveOrganization.name}
                                    </span>
                                )}
                            </FieldRow>
                            <FieldRow label="CONTACT NAME">
                                {lead.contactName}
                            </FieldRow>
                            <FieldRow label="EMAIL">
                                {lead.email}
                            </FieldRow>
                            <FieldRow label="PHONE #">
                                {lead.phoneNumber}
                            </FieldRow>
                        </>
                    ) : (
                        <>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>COMPANY</div>
                                <div className={[commonStyles.value, styles.inputContainer, styles.companyInputContainer].join(" ")}>
                                    <PipedriveCompanyInput formik={formik} label="" name="pipedriveCompanyName" />
                                    <SearchIcon className={commonStyles.link} fontSize="small" onClick={() => searchStore.setSearch(formik.values.pipedriveCompanyName!, SearchType.Leads)} />
                                </div>
                            </div>
                            <StringInputRow formik={formik} label="CONTACT NAME" name="contactName" />
                            <StringInputRow formik={formik} label="EMAIL" name="email" />
                            <PhoneNumberInputRow formik={formik} label="PHONE #" name="phoneNumber" />
                        </>
                    )}
                    {latestNote && (
                        <div >
                            <div className={commonStyles.key}>LATEST COMMENT</div>
                            <Note
                                userName={latestNote.userName}
                                text={latestNote.text}
                                createdAt={latestNote.createdAt}
                            />
                        </div>
                    )}
                    {isReadOnly ? (
                        <>
                            <FieldRow label="REMINDER">
                                {activity && formatDate(activity.dueDate)}
                            </FieldRow>
                            <FieldRow label="ACTIVITY">
                                {activity && activity.subject}
                            </FieldRow>
                        </>
                    ) : (
                        <>
                            <div className={commonStyles.row}>
                                <div className={commonStyles.key}>
                                    <label>
                                        REMINDER <Switch checked={formik.values.isSetReminder} onChange={e => {
                                            if (e.target.checked) {
                                                setReminderStore.open(date => {
                                                    formik.setFieldValue("isSetReminder", true);
                                                    formik.setFieldValue("activityDueDate", date);
                                                });
                                            } else {
                                                formik.setFieldValue("isSetReminder", false);
                                            }
                                        }} />
                                    </label>
                                </div>
                                {formik.values.isSetReminder && (
                                    <DateInput formik={formik} label="" name="activityDueDate" containerClassName={[commonStyles.value, styles.inputContainer].join(" ")} />
                                )}
                            </div>
                            {formik.values.isSetReminder && (
                                <StringInputRow formik={formik} label="ACTIVITY" name="activitySubject" />
                            )}
                        </>
                    )}
                    <div className={popupStyles.modified}>
                        <span>{lead.modifiedByUser}</span><br /><span>{formatDateTime(lead.modifiedDate)}</span>
                    </div>
                </div>
                <div className={commonStyles.separator} />
                <div className={[commonStyles.controls, styles.controls].join(" ")}>
                    <div className={commonStyles.controlGroup}>
                        {!isReadOnly && (
                            <EditControls
                                isStateChanged={formik.dirty}
                                isUpdateLoading={leadStore.isLeadsLoading.updating! || pipedriveStore.isOrganizationsLoading.creating! || pipedriveStore.isActivitiesLoading.creating! || pipedriveStore.isActivitiesLoading.updating! || pipedriveStore.isActivitiesLoading.deleting!}
                                onUpdate={formik.handleSubmit}
                                onCancel={close}
                            />
                        )}
                    </div>
                    <div className={commonStyles.controlGroup}>
                        <ExpandIcon />
                    </div>
                </div>
            </MapPopupWithFlyTo>
        </>
    );
}

export default observer(LeadPopup);

interface ILeadPopupProps {
    id: number;
}
